/**
 * Centralised client-side error reporting.
 *
 * Wires global handlers for errors that React's ErrorBoundary cannot catch:
 *   - Unhandled Promise rejections (await failures, broken fetches, saga errors)
 *   - Synchronous errors thrown outside React's render cycle (event handlers,
 *     socket callbacks, setTimeout / RAF, third-party SDK callbacks)
 *   - Lazy-loaded chunk / dynamic import failures (stale browser cache after a
 *     deploy) — auto-recovers via one-shot hard reload
 *
 * Today this logs to console and keeps the last 50 reports in memory for
 * support debugging. The same surface is the wire-up point for a future
 * Sentry/Datadog transport — add one fetch call inside `recordError`.
 */

export type ReportedErrorKind =
  | 'window-error'
  | 'unhandled-rejection'
  | 'chunk-load-failed';

export interface ReportedError {
  kind: ReportedErrorKind;
  message: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  stack?: string;
  url: string;
  timestamp: number;
}

const MAX_RECENT_ERRORS = 50;
const recentErrors: ReportedError[] = [];

const CHUNK_RELOAD_KEY = 'heltar-chunk-load-reload-ts';
const CHUNK_RELOAD_DEDUP_MS = 30_000; // don't reload twice within 30s

const INSTALL_FLAG_KEY = '__heltarErrorHandlersInstalled';

const chunkLoadPatterns: readonly RegExp[] = [
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk \d+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
];

const isChunkLoadError = (message: string): boolean =>
  chunkLoadPatterns.some(rx => rx.test(message));

const recordError = (err: ReportedError): void => {
  recentErrors.push(err);
  if (recentErrors.length > MAX_RECENT_ERRORS) recentErrors.shift();
  // Console for dev tools; future Sentry/Datadog transport hooks in here.
  console.error('[errorReporting]', err);
};

/**
 * Snapshot of the most recent errors. Useful for a "Report a bug" form or
 * a hidden debug panel.
 */
export const getRecentErrors = (): ReportedError[] => [...recentErrors];

/**
 * Detect a chunk-load failure (stale bundle in browser cache after a
 * deploy) and force a one-shot hard reload so the user picks up the
 * current chunk hashes. The reload is throttled by a sessionStorage
 * timestamp so a persistently-failing fetch doesn't loop forever.
 *
 * Returns true if the error was a chunk-load failure (caller should
 * `event.preventDefault()`).
 */
const handleChunkLoadFailure = (message: string): boolean => {
  if (!isChunkLoadError(message)) return false;

  const last = parseInt(sessionStorage.getItem(CHUNK_RELOAD_KEY) || '0', 10);
  const now = Date.now();
  if (last && now - last < CHUNK_RELOAD_DEDUP_MS) {
    // Reloaded too recently — likely a true network outage, not a stale
    // cache. Give up on auto-reload; the user will see whatever fallback
    // is in place. Reporting it stays useful.
    console.error(
      '[errorReporting] chunk load failed again within %dms — not reloading',
      CHUNK_RELOAD_DEDUP_MS,
    );
    recordError({
      kind: 'chunk-load-failed',
      message,
      url: window.location.href,
      timestamp: now,
    });
    return true;
  }

  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
  console.warn(
    '[errorReporting] chunk load failed — reloading once to refresh cache',
  );
  window.location.reload();
  return true;
};

interface InstallFlagHolder {
  [INSTALL_FLAG_KEY]?: true;
}

/**
 * Install global error handlers. Idempotent — calling twice is a no-op
 * (guards against HMR re-evaluation in dev).
 */
export const installGlobalErrorHandlers = (): void => {
  const w = window as unknown as InstallFlagHolder;
  if (w[INSTALL_FLAG_KEY]) return;
  w[INSTALL_FLAG_KEY] = true;

  window.addEventListener('error', event => {
    const errMessage =
      event.message ||
      (event.error instanceof Error ? event.error.message : '') ||
      'Unknown window error';

    if (handleChunkLoadFailure(errMessage)) {
      event.preventDefault();
      return;
    }

    recordError({
      kind: 'window-error',
      message: errMessage,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error instanceof Error ? event.error.stack : undefined,
      url: window.location.href,
      timestamp: Date.now(),
    });
  });

  window.addEventListener('unhandledrejection', event => {
    const reason: unknown = event.reason;
    let message: string;
    let stack: string | undefined;

    if (reason instanceof Error) {
      message = reason.message || reason.name || 'Unhandled rejection';
      stack = reason.stack;
    } else if (typeof reason === 'string') {
      message = reason;
    } else {
      try {
        message = JSON.stringify(reason) || 'Unhandled rejection (non-error)';
      } catch {
        message = 'Unhandled rejection (unserialisable reason)';
      }
    }

    if (handleChunkLoadFailure(message)) {
      event.preventDefault();
      return;
    }

    recordError({
      kind: 'unhandled-rejection',
      message,
      stack,
      url: window.location.href,
      timestamp: Date.now(),
    });
  });
};
